/**
 * MetaArenaSDK — 메인 클래스
 *
 * 브라우저에서 동작하는 게임(iframe)이 MetaArena 플랫폼과 통신하는 SDK입니다.
 * API Key / Secret은 필요 없습니다 — 클라이언트 사이드 점수 제출은 플랫폼이 처리합니다.
 *
 * @example
 * ```typescript
 * import { MetaArenaSDK } from '@metaarena/sdk'
 *
 * const sdk = new MetaArenaSDK()
 *
 * sdk.onStart(({ tournamentId, playerToken }) => {
 *   // playerToken: 서버 사이드 게임이라면 게임 서버로 전달
 *   startGame()
 * })
 *
 * sdk.updateScore(currentScore)  // HUD 업데이트
 *
 * sdk.gameOver({ score: currentScore, playTimeMs: elapsed })
 * ```
 */

import { Messenger } from './messenger.js'
import type { GameStartContext, GameOverPayload, GameStartMessage } from './types.js'

export const SDK_VERSION = '1.0.0'

export class MetaArenaSDK {
  private messenger: Messenger
  private startCallbacks: Array<(ctx: GameStartContext) => void> = []

  constructor() {
    this.messenger = new Messenger()
    // 플랫폼이 보내는 GAME_START 메시지 수신 등록
    this.messenger.on<GameStartMessage>('METAARENA_GAME_START', msg => {
      const ctx: GameStartContext = {
        tournamentId: msg.tournament_id,
        playerToken:  msg.player_token,
        apiBase:      msg.api_base ?? 'https://api.metaarena.io',
      }
      this.startCallbacks.forEach(cb => cb(ctx))
    })
    // 플랫폼에 SDK 로드 완료 알림 → 플랫폼이 GAME_START로 응답
    this.messenger.sendToHost({ type: 'METAARENA_READY' })
  }

  /**
   * 게임 시작 신호 수신 핸들러 등록
   * 플랫폼이 METAARENA_GAME_START를 전송하면 호출됩니다.
   */
  onStart(callback: (ctx: GameStartContext) => void): void {
    this.startCallbacks.push(callback)
  }

  /**
   * 현재 점수를 플랫폼 HUD에 실시간 반영
   * 점수가 바뀔 때마다 호출합니다. 이 메서드만으로는 점수가 제출되지 않습니다.
   */
  updateScore(score: number): void {
    this.messenger.sendToHost({ type: 'METAARENA_SCORE', score })
  }

  /**
   * 게임 종료 처리
   *
   * - 플랫폼에 GAME_OVER 신호를 전송해 게임 오버 모달을 표시합니다.
   * - 클라이언트 사이드 게임: 플랫폼이 자동으로 MetaArena API에 점수를 제출합니다.
   * - 서버 사이드 게임: UI 표시만 수행하며, 점수 제출은 게임 서버에서 직접 처리합니다.
   */
  gameOver({ score, playTimeMs }: GameOverPayload): void {
    this.messenger.sendToHost({
      type: 'METAARENA_GAME_OVER',
      score,
      play_time_ms: playTimeMs,
    })
  }

  /** SDK 및 이벤트 리스너 정리 */
  destroy(): void {
    this.messenger.destroy()
    this.startCallbacks = []
  }
}
